<template>
  <div ref="root" :class="$style['adl-chart-area']">
    <slot name="tooltipText"></slot>
    <mce-layout
      :plot-width="width"
      :plot-height="height"
      :axis-left-width="axisLeftWidth"
      :axis-right-width="axisRightWidth"
    >
      <template #legend-top>
        <mce-legend hideTopBorder :horizontal="true">
          <mce-legend-item
            :label="legendItems"
            :key-type="legendKey"
            :color="lineChartColor"
          ></mce-legend-item>
        </mce-legend>
      </template>
      <template #axis-left>
        <mce-y-axis
          scale-type="linear"
          :labels="{ unit: 'AUD', axis: yAxisLabel }"
          :tick-quantity="tickQuantityY"
          :domain="domainY"
          :range="rangeY"
          :label-format="FormatCurrency"
        >
          <template v-if="isHideFirstTick" #mce-y-tick-0
            ><span></span
          ></template>
        </mce-y-axis>
      </template>
      <template #axis-bottom>
        <div ref="xAxisParent">
          <mce-x-axis
            scale-type="linear"
            :labels="{ axis: 'Year' }"
            :domain="domainX"
            :range="changeRangeX"
          ></mce-x-axis>
        </div>
      </template>
      <template #default>
        <mce-grid
          scale-type-x="linear"
          scale-type-y="linear"
          :domain-x="domainX"
          :domain-y="domainY"
          :range-x="changeRangeX"
          :range-y="rangeY"
          :tickQuantityY="tickQuantityY"
          :outlines="{ top: true, bottom: true }"
        ></mce-grid>

        <mce-group :class="$style['hide-cursor']" class="mce-line-plot">
          <mce-pointer v-model="mouse" />
          <mce-guidelines
            v-if="mouse"
            :data="[
              { x1: mouse.offsetX, x2: mouse.offsetX, y1: 0, y2: height },
            ]"
          />
          <mce-path :style="areaStyles" :d="areaCoordinates" />
        </mce-group>
        <mce-group class="mce-line-plot">
          <mce-path :style="lineStyles" :d="lineCoordinates" />
        </mce-group>
      </template>
    </mce-layout>
  </div>
</template>

<script>
import Vue from "vue";
import MceLayout from "@mce/layout";
import MceYAxis from "@mce/y-axis";
import { format } from "d3-format";
import MceXAxis from "@mce/x-axis";
import { area, line } from "d3-shape";
import McePath from "@mce/path";
import MceGroup from "@mce/group";
import McePointer from "@mce/pointer";
import { MceLegend, MceLegendItem, MceLegendGroup } from "@mce/legend";
import MceGuidelines from "@mce/guidelines";

import MceGrid from "@mce/grid";
import _ from "lodash";
import { scaleLinear } from "d3-scale";

export default Vue.extend({
  name: "AdlChartArea",
  components: {
    MceLayout,
    MceYAxis,
    MceXAxis,
    MceGrid,
    McePath,
    MceGroup,
    MceLegend,
    MceLegendItem,
    MceLegendGroup,
    McePointer,
    MceGuidelines,
  },
  props: {
    data: {
      type: Array,
      required: true,
    },
    rangeX: {
      type: Array,
      required: true,
    },
    rangeY: {
      type: Array,
      required: true,
    },
    domainX: {
      type: Array,
      required: true,
    },
    yAxisLabel: {
      type: String,
      required: true,
    },
    domainY: {
      type: Array,
      required: true,
    },
    tickQuantityY: {
      type: Number,
      default: 5,
    },

    height: {
      type: Number,
      required: true,
    },
    currencyFormat: {
      type: String,
      required: true,
    },
    legendItems: {
      type: String,
      required: true,
    },
    legendKey: {
      type: String,
      required: true,
    },
    lineChartColor: {
      type: String,
      required: true,
    },

    axisLeftWidth: {
      type: Number,
      default: 82,
    },
    axisRightWidth: {
      type: Number,
      default: 82,
    },
    isHideFirstTick: {
      type: Boolean,
    },
  },
  // mixins: [MceMouse("line")],
  data() {
    return {
      mouseDataLine: {},
      mouseDataTitle: {},
      mouse: null,
      rootHeight: 0,
      rootWidth: 0,
      defaultFormating: ".2s",
      hoverHeight: 0,
    };
  },
  computed: {
    width() {
      return this.rootWidth - this.axisRightWidth;
    },
    changeRangeX() {
      return [0, this.rootWidth - this.axisRightWidth];
    },
    FormatCurrency: function () {
      if (this.currencyFormat) {
        return format(this.currencyFormat);
      } else {
        return format(this.defaultFormating);
      }
    },
    areaStyles() {
      return {
        stroke: "none",
        fill: "#d2d7e0",
        strokeWidth: 2,
        strokeLinecap: "round",
        strokeLinejoin: "round",
        opacity: 0.7,
      };
    },
    lineStyles() {
      return {
        stroke: "#2056af",
        fill: "none",
        strokeWidth: 2,
        strokeLinecap: "round",
        strokeLinejoin: "round",
        opacity: 0.7,
      };
    },

    xScale() {
      return scaleLinear().domain(this.domainX).range(this.changeRangeX);
    },
    yScale() {
      return scaleLinear().domain(this.domainY).range(this.rangeY);
    },
    referenceLineValue() {
      return 0;
    },
    areaCoordinates() {
      if (this.data.length > 0) {
        const path = area()
          .x((d) => this.xScale(d.value.x))
          .y0((d) => this.yScale(d.value.y))
          .y1(this.yScale(this.referenceLineValue));
        return path(this.data);
      }
    },
    lineCoordinates() {
      const path = line()
        .x((d) => this.xScale(d.value.x))
        .y((d) => this.yScale(d.value.y));
      return path(this.data);
    },
  },
  watch: {
    mouse: {
      async handler() {
        if (this.mouse) {
          let linedata = this.onMouseMove();

          this.mouseDataLine = linedata.data.yValue;
          if (!this.mouseDataLine.value.title) {
            this.mouseDataLine = null;
            this.mouseDataTitle = null;
          }
          this.$emit("setTooltipData", _.clone(this.mouseDataLine), this.mouse);
        } else {
          this.$emit("setTooltipData", null, null);
        }
      },
    },
  },
  mounted() {
    this.onResize();
    window.addEventListener("resize", _.debounce(this.onResize, 100));
  },
  beforeDestroy() {
    window.removeEventListener("resize", _.debounce(this.onResize, 100));
  },
  methods: {
    onResize() {
      const rootElement = this.$refs.root;
      if (rootElement !== undefined) {
        this.rootHeight = Number(rootElement.clientHeight) || this.rootHeight;
        this.rootWidth = Number(rootElement.clientWidth) || this.rootHeight;
      }
    },
    invertScaleBandX(x) {
      return scaleLinear()
        .domain(this.xScale.range())
        .range(this.xScale.domain())(x);
    },
    getXValue() {
      return this.invertScaleBandX(this.mouse.offsetX);
    },
    getIndexOfXAxis(value) {
      let floorValue = Math.ceil(value);
      if (value < 0) {
        return 0;
      }
      return floorValue;
    },

    getYValue() {
      const xValue = this.getXValue();
      let index = this.getIndexOfXAxis(xValue);
      if (index >= 0 && index < this.data.length) {
        let yValue = this.data[index];
        if (yValue && yValue.value && yValue.value.tooltipText) {
          for (let i = 0; i < yValue.value.tooltipText.length; i++) {
            if (yValue.value.tooltipText[i].isCurrencyType)
              yValue.value.tooltipText[i].value = format("$,")(
                parseFloat(yValue.value.tooltipText[i].value)
              );
            yValue.value.tooltipText[i].isCurrencyType = null;
          }
        }

        return {
          yValue: this.data[index],
          _dataIndex: index,
        };
      }
    },
    onMouseMove() {
      const _dataIndex = this.getXValue();
      const { id, ...rest } = this.getYValue();
      return {
        position: this.mouse,
        data: {
          ...rest,
          _dataIndex,
          _itemId: id,
        },
      };
    },
  },
});
</script>

<style lang="scss" module>
.adl-chart-area {
  .hide-cursor {
    cursor: none !important;
  }
  .add-margin {
    margin-top: 10px;
  }
  .bold-text {
    font-weight: bold;
  }
  .mce-line-plot {
    shape-rendering: auto;
  }
  .mce-grid__y-tick,
  .mce-grid__x-tick {
    stroke-dasharray: 0.1 !important;
  }
  .mce-guideline {
    stroke-width: 2 !important;
  }
  .mce-axis-x.mce-axis-x--band .mce-axis-x__tick {
    text-align: left;
  }
}
</style>
